import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ScanLine, CheckCircle2, XCircle, AlertTriangle, Camera, CameraOff, ShieldCheck,
  UserCheck, Search, Loader2, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SCANNER_DOM_ID = 'tk-staff-scanner';

const t = {
  title: 'Staff Check-in · Pointage Bénévole',
  subtitle: 'Scan attendee badges at the venue door.',
  invalidToken: 'This staff link is invalid, expired or revoked. Please contact the admin for a new link.',
  loading: 'Validating your staff link…',
  greeting: 'Hello',
  authorizedAs: 'Authorized as',
  expiresAt: 'Link expires',
  yourScans: 'Your check-ins',
  startCamera: 'Start camera',
  stopCamera: 'Stop camera',
  starting: 'Starting…',
  cameraOff: 'Camera off · point badge here',
  permissionDenied: 'Camera permission denied. Use manual entry below.',
  noCamera: 'No camera detected. Use manual entry below.',
  manualLabel: 'Manual entry (badge ID)',
  manualPlaceholder: 'TK2026-XXXXXXXX',
  manualSubmit: 'Check in',
  welcome: 'Welcome',
  alreadyTitle: 'Already checked in',
  badgeIdLabel: 'Badge',
  countryLabel: 'Country',
  scanCount: 'Scan #',
  recentTitle: 'Your recent check-ins',
  noRecent: 'No check-ins yet. Scan a badge to begin.',
  notFound: 'Badge not found. Verify the QR or use manual entry.',
  cooldownHint: 'Ready for next badge in 2s…',
};

export default function StaffCheckin() {
  const [params] = useSearchParams();
  const stafftoken = params.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [staffInfo, setStaffInfo] = useState(null);
  const [authError, setAuthError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError] = useState('');
  const [recent, setRecent] = useState([]);
  const [manual, setManual] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const scannerRef = useRef(null);
  const lastScannedRef = useRef({ value: '', at: 0 });

  // Validate staff token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!stafftoken) { setAuthError(t.invalidToken); setValidating(false); return; }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/staff/me`, { params: { token: stafftoken } });
        if (cancelled) return;
        setStaffInfo(res.data);
      } catch (err) {
        if (cancelled) return;
        setAuthError(err?.response?.data?.detail || t.invalidToken);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stafftoken]);

  const fetchRecent = useCallback(async () => {
    if (!stafftoken) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/staff/recent`, { params: { token: stafftoken } });
      setRecent(res.data?.recent || []);
      if (res.data?.checkin_count !== undefined) {
        setStaffInfo((s) => (s ? { ...s, checkin_count: res.data.checkin_count } : s));
      }
    } catch (_e) { /* ignore */ }
  }, [stafftoken]);

  useEffect(() => { if (staffInfo) fetchRecent(); }, [staffInfo, fetchRecent]);

  const submitCheckin = useCallback(async (payload) => {
    setSubmitting(true);
    setLastError('');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/staff/checkin`,
        payload,
        { params: { token: stafftoken } }
      );
      setLastResult(res.data);
      fetchRecent();
    } catch (err) {
      setLastResult(null);
      setLastError(err?.response?.data?.detail || t.notFound);
    } finally {
      setSubmitting(false);
      setCooldown(true);
      setTimeout(() => setCooldown(false), 2000);
    }
  }, [stafftoken, fetchRecent]);

  const handleScanSuccess = useCallback((decodedText) => {
    if (!decodedText || cooldown) return;
    const now = Date.now();
    if (lastScannedRef.current.value === decodedText && now - lastScannedRef.current.at < 3000) return;
    lastScannedRef.current = { value: decodedText, at: now };
    submitCheckin({ raw: decodedText });
  }, [cooldown, submitCheckin]);

  const startScanner = async () => {
    setStarting(true);
    setCameraError('');
    try {
      const scanner = new Html5Qrcode(SCANNER_DOM_ID, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: (vw, vh) => { const m = Math.min(vw, vh); const s = Math.floor(m * 0.7); return { width: s, height: s }; }, aspectRatio: 1.0 },
        handleScanSuccess,
        () => {}
      );
      setScanning(true);
    } catch (err) {
      const msg = String(err?.message || err || '');
      if (msg.toLowerCase().includes('permission')) setCameraError(t.permissionDenied);
      else if (msg.toLowerCase().includes('not found') || msg.toLowerCase().includes('no')) setCameraError(t.noCamera);
      else setCameraError(msg);
    } finally {
      setStarting(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (_e) {}
    setScanning(false);
  };

  useEffect(() => () => {
    if (scannerRef.current) {
      try { scannerRef.current.stop(); scannerRef.current.clear(); } catch (_e) {}
    }
  }, []);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    const v = manual.trim();
    if (!v) return;
    submitCheckin({ badge_id: v });
    setManual('');
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#0B0E13] text-white flex items-center justify-center p-6">
        <div className="text-center" data-testid="staff-validating">
          <Loader2 className="w-10 h-10 animate-spin text-[#FF3333] mx-auto mb-4" />
          <p className="text-sm text-gray-400">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen bg-[#0B0E13] text-white flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-rose-950/40 border border-rose-500/40 rounded-2xl p-6 text-center" data-testid="staff-auth-error">
          <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h1 className="text-xl font-extrabold mb-2">Access denied</h1>
          <p className="text-sm text-rose-200">{authError}</p>
        </div>
      </div>
    );
  }

  const lastBg = lastResult?.already_checked_in
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-emerald-50 border-emerald-300 text-emerald-900';
  const expiry = staffInfo?.expires_at ? new Date(staffInfo.expires_at) : null;

  return (
    <div className="min-h-screen bg-[#0B0E13] text-white" data-testid="staff-checkin-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-20 bg-[#0B0E13]/90 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#CD0000] to-[#FF3333] flex items-center justify-center shrink-0 shadow-lg shadow-[#CD0000]/30">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-[#FF3333] uppercase tracking-widest font-bold leading-none">Staff Check-in</p>
              <p className="text-sm font-extrabold text-white truncate" data-testid="staff-label">{staffInfo?.label || '—'}</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">{t.yourScans}</p>
            <p className="text-2xl font-extrabold text-emerald-400 leading-none" data-testid="staff-count">{staffInfo?.checkin_count ?? 0}</p>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-5 space-y-4">
        {/* Expiry */}
        {expiry && (
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {t.expiresAt}: {expiry.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
          </p>
        )}

        {/* Camera */}
        <div className="rounded-2xl bg-[#11151D] border border-white/5 overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between">
            <p className="text-xs font-bold text-gray-300 flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-[#FF3333]" />
              {scanning ? 'Aim the badge at the camera' : t.cameraOff}
            </p>
            <button
              type="button"
              onClick={scanning ? stopScanner : startScanner}
              disabled={starting}
              data-testid="staff-toggle-camera"
              className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-lg transition ${
                scanning ? 'bg-rose-500/15 text-rose-300 hover:bg-rose-500/20' : 'bg-[#CD0000] text-white hover:bg-[#A00000] disabled:opacity-60'
              }`}
            >
              {scanning ? <CameraOff className="w-3.5 h-3.5" /> : <Camera className="w-3.5 h-3.5" />}
              {starting ? t.starting : (scanning ? t.stopCamera : t.startCamera)}
            </button>
          </div>
          <div className="relative bg-black aspect-square">
            <div id={SCANNER_DOM_ID} className="w-full h-full [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover" />
            {!scanning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 gap-2 pointer-events-none">
                <ScanLine className="w-14 h-14 opacity-40" />
                <p className="text-[10px] uppercase tracking-widest font-bold">{t.cameraOff}</p>
              </div>
            )}
            {cooldown && scanning && (
              <div className="absolute bottom-3 left-3 right-3 bg-black/70 backdrop-blur text-white text-xs px-3 py-2 rounded-lg text-center">
                {t.cooldownHint}
              </div>
            )}
          </div>
          {cameraError && (
            <p data-testid="staff-camera-error" className="m-3 text-[11px] text-rose-300 bg-rose-950/40 border border-rose-500/30 rounded-lg px-3 py-2">
              {cameraError}
            </p>
          )}
        </div>

        {/* Manual entry */}
        <form onSubmit={handleManualSubmit} className="rounded-2xl bg-[#11151D] border border-white/5 p-4 space-y-2" data-testid="staff-manual-form">
          <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t.manualLabel}</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder={t.manualPlaceholder}
                data-testid="staff-manual-input"
                className="w-full bg-[#0B0E13] border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#CD0000] uppercase font-mono tracking-wider"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !manual.trim()}
              data-testid="staff-manual-submit"
              className="bg-[#CD0000] hover:bg-[#A00000] disabled:opacity-50 text-white font-bold text-sm px-4 rounded-xl transition"
            >
              {t.manualSubmit}
            </button>
          </div>
        </form>

        {/* Result */}
        {lastResult?.attendee && (
          <div data-testid="staff-result-card" className={`rounded-2xl border-2 p-4 ${lastBg}`}>
            <div className="flex items-start gap-3">
              {lastResult.attendee.photo_url ? (
                <img
                  src={lastResult.attendee.photo_url.startsWith('http') ? lastResult.attendee.photo_url : `${BACKEND_URL}${lastResult.attendee.photo_url}`}
                  alt=""
                  className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md shrink-0"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-white/70 flex items-center justify-center shrink-0">
                  {lastResult.already_checked_in
                    ? <AlertTriangle className="w-7 h-7 text-amber-600" />
                    : <CheckCircle2 className="w-7 h-7 text-emerald-600" />}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">
                  {lastResult.already_checked_in ? t.alreadyTitle : `${t.welcome} 🎉`}
                </p>
                <h3 className="text-lg font-extrabold mt-0.5 truncate">
                  {lastResult.attendee.first_name} {lastResult.attendee.last_name}
                </h3>
                <p className="text-[11px] mt-1 opacity-80 font-mono">
                  {lastResult.attendee.badge_id}
                  {lastResult.attendee.country ? ` · ${lastResult.attendee.country}` : ''}
                  {' · '}{t.scanCount}{lastResult.scan_count}
                </p>
              </div>
            </div>
          </div>
        )}

        {lastError && (
          <div data-testid="staff-error-card" className="rounded-2xl border-2 border-rose-500/40 bg-rose-950/40 p-4 flex items-start gap-3 text-rose-100">
            <XCircle className="w-6 h-6 text-rose-400 shrink-0" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">Error</p>
              <p className="text-sm mt-0.5">{lastError}</p>
            </div>
          </div>
        )}

        {/* Your recent check-ins */}
        <div className="rounded-2xl bg-[#11151D] border border-white/5 p-4">
          <h3 className="text-xs font-bold text-gray-300 mb-3 flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-emerald-400" />
            {t.recentTitle}
          </h3>
          {recent.length === 0 ? (
            <p className="text-[11px] text-gray-500 italic">{t.noRecent}</p>
          ) : (
            <ul className="divide-y divide-white/5" data-testid="staff-recent-list">
              {recent.map((r) => (
                <li key={r.registration_id} className="flex items-center gap-3 py-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
                    <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">
                      {r.first_name} {r.last_name}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      {r.country ? `${r.country} · ` : ''}
                      {r.last_scanned_at ? new Date(r.last_scanned_at).toLocaleString(undefined, { timeStyle: 'short' }) : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="text-[10px] text-gray-600 text-center pt-2 pb-6">
          TALITHA KOUMI TUG · {t.authorizedAs} <span className="text-gray-400 font-bold">{staffInfo?.label}</span> · {t.greeting}, {staffInfo?.created_by_name || 'volunteer'}
        </p>
      </main>
    </div>
  );
}
